<script setup lang="ts">
import type { ProductVariant } from '~/types/Product';
import { useCurrency } from '~/stores/currency';
import { isOriginalProductPrice } from '~/utils/helpers.ts';
import type { ProductSchema } from '@laam/cms-shared';
import { HvcIcon } from '@laam/ui/icons';
interface ProductPriceProps {
	selectedVariantData: ProductVariant | null;
	productData: ProductSchema;
}

const props = defineProps<ProductPriceProps>();
const emit = defineEmits(['updatePrices']);
const { pdpRedesignExperiment } = useStatsigExperiment();

const isOnSale = computed(() => {
	if (props.selectedVariantData) {
		return (
			parseInt(props.selectedVariantData.compare_at_price) >
			parseInt(props.selectedVariantData.price)
		);
	} else {
		return props.productData.compare_at_price > props.productData.price;
	}
});
const cheapestVariant = computed(() =>
	props.productData.variants.reduce((prev, current) =>
		parseFloat(prev.price) < parseFloat(current.price) ? prev : current,
	),
);
const showHvcDiscount = computed(() => {
	if (props.selectedVariantData) {
		if (props.selectedVariantData.reseller_price) return true;
		else return false;
	} else {
		if (cheapestVariant.value.reseller_price) return true;
		else return false;
	}
});

const hvcPrice = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.reseller_price!);
	} else {
		return cheapestVariant.value.reseller_price;
	}
});

const price = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.price);
	} else {
		return props.productData.price;
	}
});

const compareAtPrice = computed(() => {
	if (props.selectedVariantData) {
		return parseFloat(props.selectedVariantData.compare_at_price);
	} else {
		return props.productData.compare_at_price;
	}
});

function salePercentage(
	newPrice: string | number | undefined,
	oldPrice: string | number,
) {
	const p = typeof newPrice === 'string' ? parseInt(newPrice) : newPrice;
	if (!p) return 0;
	const cap = typeof oldPrice === 'string' ? parseInt(oldPrice) : oldPrice;
	return ((cap - p + 0.5) * 100) / cap;
}

const isOriginalPrice = computed(() => {
	if (props.productData) {
		return isOriginalProductPrice(
			props.productData.compare_at_price,
			props.productData.price,
		);
	}

	return false;
});

const CurrencyStore = useCurrency();
watch(
	[isOriginalPrice, hvcPrice, price, showHvcDiscount],
	([newIsOriginalPrice, newHvcPrice, newPrice, newShowHvcDiscount]) => {
		emit('updatePrices', {
			isOriginalPrice: newIsOriginalPrice,
			hvcPrice: newHvcPrice,
			price: newPrice,
			showHvcDiscount: newShowHvcDiscount,
		});
	},
	{ immediate: true },
);
</script>

<template>
	<div class="pdp__price gap-md flex flex-col">
		<div class="gap-xxs flex flex-col">
			<div class="flex w-full items-baseline gap-xs">
				<span class="font-normal text-sm text-gray-700">From</span>
				<div
					v-if="price"
					class="price"
					:class="
						isOnSale && props.productData.stock_status != INVENTORY.OOS
							? 'text-error-600'
							: 'text-gray-800'
					"
				>
					<span class="price-currency">
						{{ CurrencyStore.currencySymbol }}{{ ' ' }}
					</span>
					<span class="price-amount font-semibold">
						{{
							showHvcDiscount && props.productData.stock_status != INVENTORY.OOS
								? CurrencyStore.getFormattedPrice(hvcPrice!, true)
								: CurrencyStore.getFormattedPrice(price, !isOriginalPrice)
						}}
					</span>
				</div>
			</div>

			<div
				v-if="showHvcDiscount || isOnSale"
				class="gap-md flex lg:items-center lg:flex-row flex-col items-start"
				:class="{ '!flex-row gap-xs': pdpRedesignExperiment?.showSizeForm }"
			>
				<div v-if="isOnSale" class="gap-md flex items-center">
					<div class="compare-price">
						<span class="compare-price-currency">
							{{ CurrencyStore.currencySymbol }}
						</span>
						&nbsp;
						<span class="compare-price-amount">
							{{
								CurrencyStore.getFormattedPrice(compareAtPrice, isOriginalPrice)
							}}
						</span>
					</div>
					<div
						class="py-xxs px-sm bg-error-50 rounded-xs h-[20px] flex items-center"
					>
						<span class="sale-percentage">
							-{{ salePercentage(price, compareAtPrice).toFixed(0) }}%
						</span>
					</div>
				</div>
				<div
					v-if="
						showHvcDiscount && props.productData.stock_status != INVENTORY.OOS
					"
					class="gap-md flex items-center"
				>
					<span class="is-hvc-price">
						<span class="is-hvc-price-currency">
							{{ CurrencyStore.currencySymbol }}{{ ' ' }}
						</span>
						&nbsp;
						<span class="is-hvc-price-amount">{{
							CurrencyStore.getFormattedPrice(price, !isOriginalPrice)
						}}</span>
					</span>
					<div
						class="flex gap-xxs py-xxs px-sm bg-violet-50 rounded-xs items-center h-5xl"
					>
						<HvcIcon :fill="'#7839EE'" :width="12" :height="12" />
						<span class="hvc-percentage">
							-{{ salePercentage(hvcPrice, price).toFixed(0) }}%
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.pc-text {
	text-justify: distribute;
}

.price {
	@apply text-lg font-semibold;
}

.compare-price {
	@apply line-through flex lg:text-md font-normal tracking-[-0.6px] text-gray-700 text-sm;
}

.sale-percentage {
	@apply text-xs font-semibold text-error-600;
}

.is-hvc-price {
	@apply line-through flex lg:text-md font-normal tracking-[-0.6px] text-gray-700 text-sm;
}
.hvc-percentage {
	@apply text-xs font-semibold text-violet-600;
}

@media (max-width: 380px) {
	.price {
		@apply text-md;
	}
}

@media (max-width: 330px) {
	.price {
		@apply text-sm;
	}
}
</style>
